import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { createRoot } from 'react-dom/client'
import { flushSync } from 'react-dom'
import { toastr } from 'react-redux-toastr'
import { ChevronLeft, ChevronRight, FileDown, Loader2, Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import * as Query from '../../../api/gql/query'
import ConfirmDialog from '../components/ConfirmDialog'
import { COLUMN_ORDER, columnLabelKey } from '../detail/gantt-columns'
import type { ColumnId } from '../lib/constants'
import { formatIsoDate, type ISODate } from '../lib/format'
import type { DateRange } from '../lib/geometry'
import type { LocalLink } from '../lib/predecessors'
import type { Tree, VisibleRow } from '../lib/tree'
import type { Schedule, ScheduleActivity, ScheduleDependency } from '../types'
import { buildPrintLinks, buildPrintRows } from './print-data'
import type { PrintLabels } from './print-labels'
import { buildPrintLayout } from './print-layout'
import { loadPrintOptions, savePrintOptions, type PrintOptions } from './print-options'
import { ALWAYS_PRINT_COLUMNS } from './PrintColumnsPicker'
import PrintOptionsForm from './PrintOptionsForm'
import PrintPage from './PrintPage'
import PrintPortal from './PrintPortal'
import { exportPagesToPdf, pdfFileName, PdfFontError, PdfPageError } from './pdf-export'

/** Everything the printable view needs, snapshotted by the Gantt at the moment the dialog opens. */
export interface PrintSource {
  schedule: Schedule
  rows: VisibleRow<ScheduleActivity>[]
  tree: Tree<ScheduleActivity>
  byId: Map<string, ScheduleActivity>
  dependencies: ScheduleDependency[]
  collapsedIds: Set<string>
  predecessorsByActivity: Map<string, { local: LocalLink[] }>
  columns: ColumnId[]
  columnWidths: Record<ColumnId, number>
  range: DateRange
  today: ISODate
  hasBaseline: boolean
  /** Visible-row index per activity id, as the Gantt already computed it. */
  rowIndexById: Map<string, number>
  /** The toolbar's own baseline toggle — the dialog opens mirroring what the screen shows. */
  showBaseline: boolean
  /** The toolbar's own critical-path toggle, mirrored the same way. */
  showCriticalPath: boolean
}

interface PrintDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  source: PrintSource
}

/** Preview width in CSS px; the page is scaled down to it, never re-laid out. */
const PREVIEW_W = 640
/** Above this many pages the user is asked to confirm before printing or exporting. */
const MANY_PAGES = 300

const PrintDialog: React.FC<PrintDialogProps> = ({ open, onOpenChange, source }) => {
  const { t, i18n } = useTranslation()
  const { data: meData } = useQuery(Query.ME)
  const projectName: string = meData?.me?.currentProject?.name ?? ''

  // Six options are remembered between sessions; baseline and critical are not — they mirror the
  // screen's own toggles (spec §2) and are re-seeded from `source` on every open. The dialog is
  // mounted only while open, so the initialiser runs once per open and this stays honest.
  const [options, setOptions] = useState<PrintOptions>(() => ({
    ...loadPrintOptions(),
    includeBaseline: source.showBaseline && source.hasBaseline,
    includeCritical: source.showCriticalPath,
  }))
  // Columns to print: seeded from what the Gantt shows right now (spec §0.3), kept only for this
  // dialog. The row number and the name cannot be dropped.
  const [printColumns, setPrintColumns] = useState<ColumnId[]>(() => COLUMN_ORDER.filter((c) => source.columns.indexOf(c) >= 0 || ALWAYS_PRINT_COLUMNS.indexOf(c) >= 0))
  const toggleColumn = (id: ColumnId, checked: boolean) => {
    if (ALWAYS_PRINT_COLUMNS.indexOf(id) >= 0) return
    setPrintColumns((cur) => (checked ? COLUMN_ORDER.filter((c) => c === id || cur.indexOf(c) >= 0) : cur.filter((c) => c !== id)))
  }
  const [pageIndex, setPageIndex] = useState(0)
  const [printing, setPrinting] = useState(false)
  const [exporting, setExporting] = useState<{ done: number; total: number } | null>(null)
  const [pendingAction, setPendingAction] = useState<'print' | 'export' | null>(null)
  const signalRef = useRef({ cancelled: false })

  // Persisting outside the updater on purpose: React may invoke a state updater twice.
  const update = (patch: Partial<PrintOptions>) => {
    const next = { ...options, ...patch }
    setOptions(next)
    savePrintOptions(next)
  }

  const rows = useMemo(
    () => buildPrintRows(source.rows, source.predecessorsByActivity, source.tree),
    [source.rows, source.predecessorsByActivity, source.tree],
  )
  const effective = useMemo<PrintOptions>(
    () => ({ ...options, includeBaseline: options.includeBaseline && source.hasBaseline }),
    [options, source.hasBaseline],
  )
  const links = useMemo(
    () => buildPrintLinks({
      dependencies: source.dependencies, byId: source.byId, tree: source.tree,
      collapsedIds: source.collapsedIds, rowIndexById: source.rowIndexById, includeCritical: effective.includeCritical,
    }),
    [source.dependencies, source.byId, source.tree, source.collapsedIds, source.rowIndexById, effective.includeCritical],
  )
  const layout = useMemo(
    () => buildPrintLayout(effective, {
      rowCount: rows.length, columns: printColumns,
      columnWidths: source.columnWidths, totalDays: source.range.totalDays,
    }),
    [effective, rows.length, printColumns, source.columnWidths, source.range.totalDays],
  )
  const pageCount = layout.pages.length
  useEffect(() => { setPageIndex((i) => Math.max(0, Math.min(i, pageCount - 1))) }, [pageCount])

  const locale = t('date_locale') || i18n.language
  const labels = useMemo<PrintLabels>(() => ({
    columns: COLUMN_ORDER.reduce((o, c) => ({ ...o, [c]: t(columnLabelKey[c]) }), {} as Record<ColumnId, string>),
    legend: {
      task: t('Schedule_legend_task'), critical: t('Schedule_legend_critical'), milestone: t('Schedule_legend_milestone'),
      summary: t('Schedule_legend_summary'), baseline: t('Schedule_legend_baseline'), constraint: t('Schedule_legend_constraint'),
    },
    health: {
      COMPLETED: t('Schedule_health_COMPLETED'), ON_TRACK: t('Schedule_health_ON_TRACK'),
      AT_RISK: t('Schedule_health_AT_RISK'), DELAYED: t('Schedule_health_DELAYED'),
    },
    today: t('Schedule_today'),
    daysShort: t('Schedule_unit_days_short'),
    page: (n: number, total: number) => t('Schedule_print_page', { n, total }),
  }), [t])

  const header = useMemo(() => {
    const span = source.schedule.start && source.schedule.finish
      ? `${formatIsoDate(t, source.schedule.start)} – ${formatIsoDate(t, source.schedule.finish)}`
      : ''
    return {
      title: source.schedule.name,
      status: t(`Schedule_status_${source.schedule.status}`),
      meta: [projectName, span, t('Schedule_print_printed_on', { date: formatIsoDate(t, source.today) })].filter(Boolean).join(' · '),
    }
  }, [source.schedule, source.today, projectName, t])

  const pageEl = (i: number) => (
    <PrintPage
      key={i} layout={layout} page={layout.pages[i]} options={effective} rows={rows} links={links}
      range={source.range} today={source.today} statusDate={source.schedule.statusDate}
      header={header} labels={labels} locale={locale} t={t}
    />
  )

  const runExport = async () => {
    signalRef.current = { cancelled: false }
    const signal = signalRef.current
    setExporting({ done: 0, total: pageCount })
    try {
      await exportPagesToPdf({
        pageW: layout.pageW, pageH: layout.pageH, orientation: effective.orientation, total: pageCount,
        fileName: pdfFileName(source.schedule.name, source.today),
        renderPage: (i, host) => {
          const root = createRoot(host)
          flushSync(() => root.render(pageEl(i)))
          return () => root.unmount()
        },
        onProgress: (done, total) => setExporting({ done, total }),
        signal,
      })
    } catch (e: any) {
      if (e instanceof PdfFontError) toastr.error(t('Common_error'), t('Schedule_print_font_error'))
      else if (e instanceof PdfPageError) toastr.error(t('Common_error'), t('Schedule_print_pdf_error', { page: e.page, message: e.message }))
      else toastr.error(t('Common_error'), e?.message || String(e))
    } finally {
      setExporting(null)
    }
  }

  const runAction = (kind: 'print' | 'export') => { if (kind === 'print') setPrinting(true); else void runExport() }
  const requestAction = (kind: 'print' | 'export') => { if (pageCount > MANY_PAGES) setPendingAction(kind); else runAction(kind) }
  const confirmPending = () => { const kind = pendingAction; setPendingAction(null); if (kind) runAction(kind) }
  const cancelExport = () => { signalRef.current.cancelled = true }

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      if (exporting) signalRef.current.cancelled = true
      setPrinting(false)
      setPendingAction(null)
    }
    onOpenChange(next)
  }

  const isEmpty = rows.length === 0
  const busy = !!exporting || printing
  const previewScale = PREVIEW_W / layout.pageW
  // The clamping effect only lands after the render that shrank the layout — clamp here too.
  const safeIndex = Math.max(0, Math.min(pageIndex, pageCount - 1))
  // An export ticks `exporting` once per page, re-rendering this component each time. The preview
  // is a whole page of SVG, so it is memoised on what the page actually draws — progress alone
  // must not rebuild it.
  const previewEl = useMemo(
    () => pageEl(safeIndex),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layout, safeIndex, effective, rows, links, source.range, source.today, source.schedule.statusDate, header, labels, locale, t],
  )

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t('Schedule_print_title')}</DialogTitle>
            <DialogDescription>{t('Schedule_print_dialog_description')}</DialogDescription>
          </DialogHeader>

          {isEmpty ? (
            <p className="text-sm text-muted-foreground py-8 text-center">{t('Schedule_print_empty')}</p>
          ) : (
            <div className="flex gap-4">
              <PrintOptionsForm
                options={options}
                onChange={update}
                hasBaseline={source.hasBaseline}
                disabled={busy}
                columns={printColumns}
                onToggleColumn={toggleColumn}
                stripCount={layout.strips.length}
                summary={[
                  t('Schedule_print_summary_pages', { count: pageCount }),
                  t('Schedule_print_summary_rows', { count: rows.length }),
                  t('Schedule_print_summary_cols', { count: printColumns.length }),
                ].join(' · ')}
              />

              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-muted-foreground mb-1">{t('Schedule_print_preview')}</div>
                <div className="bg-muted rounded-md overflow-auto max-h-[60vh] p-3">
                  {/* ui-drift: dynamic — the preview box is the paper's own aspect ratio at the current scale */}
                  <div className="overflow-hidden" style={{ width: PREVIEW_W, height: layout.pageH * previewScale }}>
                    {/* ui-drift: dynamic — one real page scaled down; re-laying it out would not be a preview */}
                    <div className="origin-top-left" style={{ transform: `scale(${previewScale})`, width: layout.pageW, height: layout.pageH }}>
                      {previewEl}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-2 pt-2">
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setPageIndex(safeIndex - 1)} disabled={safeIndex === 0}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm tabular-nums">{`${safeIndex + 1} / ${pageCount}`}</span>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setPageIndex(safeIndex + 1)} disabled={safeIndex >= pageCount - 1}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            {exporting && (
              <Button variant="link" size="sm" className="mr-auto" onClick={cancelExport}>{t('Schedule_print_cancel_export')}</Button>
            )}
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={!!exporting}>{t('Common_cancel')}</Button>
            <Button variant="outline" onClick={() => requestAction('print')} disabled={busy || isEmpty}>
              {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              {t('Schedule_print_action')}
            </Button>
            <Button onClick={() => requestAction('export')} disabled={busy || isEmpty}>
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              {exporting ? t('Schedule_print_exporting', { done: exporting.done, total: exporting.total }) : t('Schedule_print_export_pdf')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingAction !== null}
        title={t('Schedule_print_title')}
        description={t('Schedule_print_many_pages', { pages: pageCount })}
        confirmLabel={t('Common_yes')}
        onCancel={() => setPendingAction(null)}
        onConfirm={confirmPending}
      />

      {printing && (
        <PrintPortal
          pages={layout.pages.map((_, i) => pageEl(i))}
          paper={effective.paper}
          orientation={effective.orientation}
          onDone={() => setPrinting(false)}
        />
      )}
    </>
  )
}

export default PrintDialog
